import React, { useEffect, useState } from 'react';
import { BookOpen, Pin, Eye, ShoppingCart, DollarSign, TrendingUp, Clock, Activity, Zap, ChevronRight, UserCheck, ArrowUp, ArrowDown, Play, Pause, Trash2, Calendar, CreditCard, Info, CheckCircle2, LayoutList, ExternalLink, Database, Server, History, Receipt, AlertCircle, RefreshCw, Mail, Settings, X, BarChart3, MousePointer, Sparkles, SplitSquareHorizontal, Edit, Target, Timer, ArrowRight, AlertTriangle, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface FeaturedCoursesManagerProps {
  courses: any[];
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// Mock Audit Logs
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: FC-01 завершен. Активирован FC-03.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для FC-02. Статус изменен на "Активен".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 45 SMS-уведомлений о старте кампаний.',
  status: 'info'
}];
export function FeaturedCoursesManager({
  courses: initialCourses,
  setDetailPanel,
  success
}: FeaturedCoursesManagerProps) {
  const [campaigns, setCampaigns] = useState(initialCourses);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'timing' | 'analytics' | 'ab_test' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [logs, setLogs] = useState(initialLogs);
  const [engineStatus, setEngineStatus] = useState<'active' | 'degraded' | 'error'>('active');
  // Sync with props
  useEffect(() => {
    setCampaigns(initialCourses);
  }, [initialCourses]);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  const handleAction = (action: string, campaign: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view_course':
        window.open(`/course/${campaign.id}`, '_blank');
        break;
      case 'view_admin':
        setDetailPanel({
          type: 'featured-course',
          title: campaign.course,
          data: campaign
        });
        break;
      case 'view_advertiser':
        setDetailPanel({
          type: 'user-profile',
          title: campaign.advertiser,
          data: {
            name: campaign.advertiser,
            type: campaign.advertiserType
          }
        });
        break;
      case 'view_slot':
        setDetailPanel({
          type: 'slot-detail',
          title: campaign.slot,
          data: {
            name: campaign.slot
          }
        });
        break;
      case 'analytics':
        setSelectedCampaign(campaign);
        setActiveModal('analytics');
        break;
      case 'payment':
        setSelectedCampaign(campaign);
        setActiveModal('payment');
        break;
      case 'timing':
        setSelectedCampaign(campaign);
        setActiveModal('timing');
        break;
      case 'ab_test':
        setSelectedCampaign(campaign);
        setActiveModal('ab_test');
        break;
      case 'activate':
        if (campaign.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${campaign.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        success(`Кампания ${campaign.id} активирована`);
        addLog('activation', `Кампания ${campaign.id} успешно активирована`, 'success');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'active'
        } : c));
        break;
      case 'pause':
        success(`Кампания ${campaign.id} приостановлена`);
        addLog('pause', `Кампания ${campaign.id} поставлена на паузу`, 'warning');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'paused'
        } : c));
        break;
      case 'fastTrack':
        success(`Fast Track включен для ${campaign.id}`);
        addLog('fast_track', `Fast Track включен для ${campaign.id}`, 'info');
        break;
      case 'delete':
        if (confirm(`Удалить кампанию ${campaign.id}?`)) {
          success(`Кампания ${campaign.id} удалена`);
          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        }
        break;
    }
  };
  // --- METRICS CALCULATION ---
  const totalImpressions = campaigns.reduce((acc, c) => acc + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + (c.clicks || 0), 0);
  const totalPurchases = campaigns.reduce((acc, c) => acc + (c.purchases || 0), 0);
  const totalRevenue = campaigns.reduce((acc, c) => acc + (c.status === 'active' ? c.price : 0), 0);
  const totalCourseRevenue = totalPurchases * 15000; // Mock avg check
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : '0.00';
  const avgCr = totalClicks > 0 ? (totalPurchases / totalClicks * 100).toFixed(2) : '0.00';
  const avgCpa = totalPurchases > 0 ? Math.round(totalRevenue / totalPurchases) : 0;
  const avgRpm = totalImpressions > 0 ? Math.round(totalRevenue / totalImpressions * 1000) : 0;
  const renderKpis = () => <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-id="element-11245">
      <Card padding="sm" hoverable className="bg-indigo-50 border-indigo-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11246">
        <div className="text-[10px] text-indigo-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11247">
          <Pin className="w-3 h-3" data-id="element-11248" /> Активные
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11249">
          {campaigns.filter(c => c.status === 'active').length}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-blue-50 border-blue-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11250">
        <div className="text-[10px] text-blue-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11251">
          <Eye className="w-3 h-3" data-id="element-11252" /> Показы
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11253">
          {totalImpressions.toLocaleString()}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-sky-50 border-sky-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11254">
        <div className="text-[10px] text-sky-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11255">
          <MousePointer className="w-3 h-3" data-id="element-11256" /> Клики
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11257">
          {totalClicks.toLocaleString()}
        </div>
        <div className="text-[10px] text-sky-600 mt-1" data-id="element-11258">CTR {avgCtr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-emerald-50 border-emerald-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11259">
        <div className="text-[10px] text-emerald-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11260">
          <ShoppingCart className="w-3 h-3" data-id="element-11261" /> Покупки
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11262">
          {totalPurchases.toLocaleString()}
        </div>
        <div className="text-[10px] text-emerald-600 mt-1" data-id="element-11263">CR {avgCr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-green-50 border-green-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11264">
        <div className="text-[10px] text-green-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11265">
          <DollarSign className="w-3 h-3" data-id="element-11266" /> Доход (Ads)
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11267">
          {formatCurrency(totalRevenue)}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-amber-50 border-amber-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11268">
        <div className="text-[10px] text-amber-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11269">
          <Target className="w-3 h-3" data-id="element-11270" /> CPA
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11271">
          {formatCurrency(avgCpa)}
        </div>
        <div className="text-[10px] text-amber-600 mt-1" data-id="element-11272">Цена за покупку</div>
      </Card>
      <Card padding="sm" hoverable className="bg-purple-50 border-purple-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11273">
        <div className="text-[10px] text-purple-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11274">
          <Activity className="w-3 h-3" data-id="element-11275" /> RPM
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11276">
          {formatCurrency(avgRpm)}
        </div>
        <div className="text-[10px] text-purple-600 mt-1" data-id="element-11277">
          Доход на 1000 пок.
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-slate-50 border-slate-200 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11278">
        <div className="text-[10px] text-slate-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11279">
          <CreditCard className="w-3 h-3" data-id="element-11280" /> Ср. чек курса
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11281">
          {formatCurrency(15000)}
        </div>
      </Card>
    </div>;
  const renderAiRecommendations = () => <Card padding="sm" className="mb-6 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-id="element-11282">
      <div className="flex items-start gap-4" data-id="element-11283">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0" data-id="element-11284">
          <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-11285" />
        </div>
        <div className="flex-1" data-id="element-11286">
          <h3 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-11287">
            AI Рекомендации для платформы
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11288">
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11289">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11290">
                Повышение цены слота
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11291">
                Слот "Главная страница - TOP" перегружен (очередь 4+).
                Рекомендуется повысить базовую цену на 15% для балансировки
                спроса.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11292">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11293">
                Оптимизация креативов
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11294">
                Кампания FC-02 имеет CTR ниже среднего (3.4%). Предложите
                рекламодателю запустить A/B тест с новым креативом.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11295">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11296">
                Upsell возможность
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11297">
                Внешний рекламодатель "Институт Астрологии" имеет высокий
                бюджет. Предложите пакетное размещение "Сайдбар + Email".
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>;
  const renderTable = () => <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-11298">
      <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-11299">
        <div className="flex items-center gap-3" data-id="element-11300">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-11301">
            <BookOpen className="w-4 h-4 text-indigo-600" data-id="element-11302" />
          </div>
          <div data-id="element-11303">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-11304">
              Управление кампаниями курсов
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-11305">
              Ads Manager для образовательных продуктов
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-11306">
          <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-11308" />} onClick={() => setActiveModal('logs')} data-id="element-11307">
            Audit Logs
          </Button>
          <Button variant="primary" size="sm" icon={<Pin className="w-4 h-4" data-id="element-11310" />} data-id="element-11309">
            Создать кампанию
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-11311">
        <table className="w-full text-left whitespace-nowrap" data-id="element-11312">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-11313">
            <tr data-id="element-11314">
              <th className="px-4 py-3 font-medium" data-id="element-11315">Кампания / Курс</th>
              <th className="px-4 py-3 font-medium" data-id="element-11316">Рекламодатель</th>
              <th className="px-4 py-3 font-medium" data-id="element-11317">Слот / Позиция</th>
              <th className="px-4 py-3 font-medium" data-id="element-11318">Таймер / Период</th>
              <th className="px-4 py-3 font-medium" data-id="element-11319">Коммерция</th>
              <th className="px-4 py-3 font-medium" data-id="element-11320">
                Воронка (Показы → Клики → Покупки)
              </th>
              <th className="px-4 py-3 font-medium" data-id="element-11321">Статус / Очередь</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-11322">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-11323">
            {campaigns.map(c => {
            const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100).toFixed(1) : '0.0';
            const cr = c.clicks > 0 ? (c.purchases / c.clicks * 100).toFixed(1) : '0.0';
            const isPaid = c.paymentStatus === 'paid';
            // Timer logic simulation
            let timerColor = 'text-slate-500';
            let timerText = 'Ожидание';
            if (c.status === 'active') {
              timerColor = 'text-emerald-600';
              timerText = 'Осталось 14 дн.';
            } else if (c.status === 'queued') {
              timerColor = 'text-amber-600';
              timerText = 'Старт через 4 дн.';
            }
            return <tr key={c.id} className="hover:bg-slate-50 transition-colors group bg-white" data-id="element-11324">
                  {/* Course Info */}
                  <td className="px-4 py-3" data-id="element-11325">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11326">
                      <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view_admin', c)} data-id="element-11327">
                        {c.id}
                      </span>
                      {c.creatives?.length > 1 && <Badge variant="pro" size="sm" className="text-[9px] py-0 cursor-pointer" onClick={() => handleAction('ab_test', c)} data-id="element-11328">
                          <SplitSquareHorizontal className="w-2.5 h-2.5 mr-0.5" data-id="element-11329" />{' '}
                          A/B Test
                        </Badge>}
                    </div>
                    <div className="text-sm font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view_course', c)} data-id="element-11330">
                      {c.course} <ExternalLink className="w-3 h-3 opacity-50" data-id="element-11331" />
                    </div>
                  </td>

                  {/* Advertiser */}
                  <td className="px-4 py-3" data-id="element-11332">
                    <div className="text-xs font-medium text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5" onClick={() => handleAction('view_advertiser', c)} data-id="element-11333">
                      <UserCheck className="w-3.5 h-3.5 text-slate-400" data-id="element-11334" />
                      {c.advertiser}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-11335">
                      {c.advertiserType === 'astrologer' ? 'Астролог платформы' : 'Внешний рекламодатель'}
                    </div>
                  </td>

                  {/* Slot & Position */}
                  <td className="px-4 py-3" data-id="element-11336">
                    <div className="text-xs font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5 mb-1" onClick={() => handleAction('view_slot', c)} data-id="element-11337">
                      <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-11338" />
                      {c.slot}
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-11339">
                      {c.position}
                    </Badge>
                  </td>

                  {/* Timer & Period */}
                  <td className="px-4 py-3" data-id="element-11340">
                    <div className={`text-xs font-bold flex items-center gap-1.5 mb-1 cursor-pointer hover:underline ${timerColor}`} onClick={() => handleAction('timing', c)} data-id="element-11341">
                      <Timer className="w-3.5 h-3.5" data-id="element-11342" />
                      {timerText}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-11343">
                      <Calendar className="w-3 h-3" data-id="element-11344" />
                      {c.periodStart} — {c.periodEnd}
                    </div>
                  </td>

                  {/* Commerce */}
                  <td className="px-4 py-3" data-id="element-11345">
                    <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-11346">
                      {formatCurrency(c.price)}
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', c)} data-id="element-11347">
                      <Receipt className="w-2.5 h-2.5" data-id="element-11348" />
                      {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                    </div>
                  </td>

                  {/* Funnel Metrics */}
                  <td className="px-4 py-3" data-id="element-11349">
                    <div className="flex items-center gap-4 cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => handleAction('analytics', c)} data-id="element-11350">
                      <div className="flex flex-col items-center w-16" data-id="element-11351">
                        <span className="text-xs font-bold text-slate-900" data-id="element-11352">
                          {c.impressions.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11353">
                          Показы
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-11354" />
                      <div className="flex flex-col items-center w-16" data-id="element-11355">
                        <span className="text-xs font-bold text-blue-600" data-id="element-11356">
                          {c.clicks.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11357">
                          CTR {ctr}%
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-11358" />
                      <div className="flex flex-col items-center w-16" data-id="element-11359">
                        <span className="text-xs font-bold text-emerald-600" data-id="element-11360">
                          {c.purchases.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11361">
                          CR {cr}%
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Status & Queue */}
                  <td className="px-4 py-3" data-id="element-11362">
                    <div className="flex flex-col gap-1.5 items-start" data-id="element-11363">
                      <Badge variant={c.status === 'active' ? 'success' : c.status === 'queued' ? 'warning' : 'neutral'} size="sm" data-id="element-11364">
                        {c.status === 'active' ? 'Транслируется' : c.status === 'queued' ? 'В очереди' : 'Пауза'}
                      </Badge>

                      {c.queuePosition && <div className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-100 flex items-center gap-1" onClick={() => handleAction('timing', c)} data-id="element-11365">
                          <Layers className="w-3 h-3" data-id="element-11366" />
                          Очередь: #{c.queuePosition}
                        </div>}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" data-id="element-11367">
                    <div className="flex items-center justify-end gap-1" data-id="element-11368">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Аналитика" onClick={() => handleAction('analytics', c)} data-id="element-11369">
                        <BarChart3 className="w-3.5 h-3.5" data-id="element-11370" />
                      </Button>
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => handleAction('view_admin', c)} data-id="element-11371">
                        <Edit className="w-3.5 h-3.5" data-id="element-11372" />
                      </Button>
                      {c.status === 'active' ? <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', c)} data-id="element-11373">
                          <Pause className="w-3.5 h-3.5" data-id="element-11374" />
                        </Button> : <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-emerald-500 hover:bg-emerald-50" title="Активировать" onClick={e => handleAction('activate', c, e)} data-id="element-11375">
                          <Play className="w-3.5 h-3.5 fill-current" data-id="element-11376" />
                        </Button>}
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', c)} data-id="element-11377">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-11378" />
                      </Button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-11379">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11380">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-11381">
              <div className="flex items-center gap-3" data-id="element-11382">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-11383" />
                <div data-id="element-11384">
                  <h3 className="font-bold" data-id="element-11385">Audit Logs: Ads Manager</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-11386">
                    Журнал событий рекламной платформы
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-11388" />} onClick={() => setActiveModal(null)} data-id="element-11387" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-11389">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-11390">
                  <div className="text-slate-500 shrink-0" data-id="element-11391">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-11392">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-11393">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedCampaign && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11394">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11395">
              <div className="flex items-center gap-2" data-id="element-11396">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-11397" />
                <h3 className="font-bold text-slate-900" data-id="element-11398">
                  Детали оплаты кампании
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11400" />} onClick={() => setActiveModal(null)} data-id="element-11399" />
            </div>
            <div className="p-6 space-y-6" data-id="element-11401">
              <div className="text-center" data-id="element-11402">
                <div className="text-sm text-slate-500 mb-1" data-id="element-11403">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-11404">
                  {formatCurrency(selectedCampaign.price || 0)}
                </div>
                <Badge variant={selectedCampaign.paymentStatus === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-11405">
                  {selectedCampaign.paymentStatus === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-11406">
                <div className="flex justify-between" data-id="element-11407">
                  <span className="text-slate-500" data-id="element-11408">ID Кампании</span>
                  <span className="font-bold text-slate-900" data-id="element-11409">
                    {selectedCampaign.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11410">
                  <span className="text-slate-500" data-id="element-11411">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-11412">
                    {selectedCampaign.invoiceId || 'INV-PENDING'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11413">
                  <span className="text-slate-500" data-id="element-11414">Транзакция</span>
                  <span className="font-mono text-slate-900" data-id="element-11415">
                    {selectedCampaign.paymentId || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11416">
                  <span className="text-slate-500" data-id="element-11417">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-11418">
                    {selectedCampaign.advertiser}
                  </span>
                </div>
              </div>

              {selectedCampaign.paymentStatus !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-11419">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-11420" />
                  <div className="text-sm text-amber-800" data-id="element-11421">
                    <strong data-id="element-11422">Блокировка активации:</strong> Кампания не будет
                    запущена до подтверждения оплаты.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-11423">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-11424">
                  Закрыть
                </Button>
                {selectedCampaign.paymentStatus !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              success('Оплата подтверждена');
              addLog('payment', `Ручное подтверждение оплаты для ${selectedCampaign.id}`, 'success');
              setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? {
                ...c,
                paymentStatus: 'paid',
                paymentId: 'PAY-MANUAL-' + Date.now()
              } : c));
              setActiveModal(null);
            }} data-id="element-11425">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedCampaign && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11426">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11427">
              <div className="flex items-center gap-2" data-id="element-11428">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-11429" />
                <h3 className="font-bold text-slate-900" data-id="element-11430">Тайминг и Очередь</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11432" />} onClick={() => setActiveModal(null)} data-id="element-11431" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-11433">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11434">
                <span className="text-slate-500" data-id="element-11435">Статус</span>
                <Badge variant={selectedCampaign.status === 'active' ? 'success' : 'warning'} data-id="element-11436">
                  {selectedCampaign.status === 'active' ? 'Транслируется' : 'В очереди'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11437">
                <span className="text-slate-500" data-id="element-11438">Период</span>
                <span className="font-medium text-slate-900" data-id="element-11439">
                  {selectedCampaign.periodStart} — {selectedCampaign.periodEnd}
                </span>
              </div>

              {selectedCampaign.status === 'queued' && <>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11440">
                    <span className="text-slate-500" data-id="element-11441">Позиция в очереди</span>
                    <span className="font-bold text-indigo-600" data-id="element-11442">
                      #{selectedCampaign.queuePosition}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11443">
                    <span className="text-slate-500" data-id="element-11444">Ожидаемый старт</span>
                    <span className="font-bold text-slate-900" data-id="element-11445">
                      {selectedCampaign.expectedStartAt || 'Неизвестно'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11446">
                    <span className="text-slate-500" data-id="element-11447">Время ожидания</span>
                    <span className="font-medium text-amber-600" data-id="element-11448">
                      {selectedCampaign.waitTime || '—'}
                    </span>
                  </div>
                </>}

              <div className="mt-6 bg-indigo-50 p-4 rounded-xl text-xs text-indigo-800 flex gap-2" data-id="element-11449">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-11450" />
                <p data-id="element-11451">
                  Система автоматически активирует кампанию при освобождении
                  слота "{selectedCampaign.slot}" (onExpire event).
                </p>
              </div>
            </div>
          </Card>}

        {/* ANALYTICS MODAL */}
        {activeModal === 'analytics' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11452">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11453">
              <div className="flex items-center gap-2" data-id="element-11454">
                <BarChart3 className="w-5 h-5 text-indigo-600" data-id="element-11455" />
                <h3 className="font-bold text-slate-900" data-id="element-11456">
                  Аналитика кампании: {selectedCampaign.course}
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11458" />} onClick={() => setActiveModal(null)} data-id="element-11457" />
            </div>
            <div className="p-6" data-id="element-11459">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-11460">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11461">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11462">Показы</div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-11463">
                    {selectedCampaign.impressions.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11464">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11465">Клики</div>
                  <div className="text-2xl font-bold text-blue-600" data-id="element-11466">
                    {selectedCampaign.clicks.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11467">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11468">CTR</div>
                  <div className="text-2xl font-bold text-indigo-600" data-id="element-11469">
                    {selectedCampaign.impressions > 0 ? (selectedCampaign.clicks / selectedCampaign.impressions * 100).toFixed(2) : '0.00'}
                    %
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11470">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11471">Покупки</div>
                  <div className="text-2xl font-bold text-emerald-600" data-id="element-11472">
                    {selectedCampaign.purchases.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="h-48 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-6" data-id="element-11473">
                <div className="text-slate-400 flex items-center gap-2" data-id="element-11474">
                  <BarChart3 className="w-5 h-5" data-id="element-11475" />
                  <span data-id="element-11476">График конверсий (Mock)</span>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-11477">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-11478">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}

        {/* A/B TEST MODAL */}
        {activeModal === 'ab_test' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11479">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11480">
              <div className="flex items-center gap-2" data-id="element-11481">
                <SplitSquareHorizontal className="w-5 h-5 text-indigo-600" data-id="element-11482" />
                <h3 className="font-bold text-slate-900" data-id="element-11483">
                  A/B Тестирование креативов
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11485" />} onClick={() => setActiveModal(null)} data-id="element-11484" />
            </div>
            <div className="p-6" data-id="element-11486">
              <p className="text-sm text-slate-600 mb-6" data-id="element-11487">
                Сравнение эффективности различных креативов для кампании{' '}
                <strong data-id="element-11488">{selectedCampaign.id}</strong>.
              </p>

              <div className="space-y-4" data-id="element-11489">
                {selectedCampaign.creatives?.map((cr: any, idx: number) => <div key={cr.id} className={`p-4 rounded-xl border ${idx === 0 ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-200 bg-white'} flex items-center justify-between`} data-id="element-11490">
                    <div className="flex items-center gap-4" data-id="element-11491">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${idx === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-11492">
                        {idx === 0 ? 'A' : 'B'}
                      </div>
                      <div data-id="element-11493">
                        <div className="font-bold text-slate-900" data-id="element-11494">
                          {cr.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-11495">
                          ID: {cr.id}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6 text-center" data-id="element-11496">
                      <div data-id="element-11497">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-11498">
                          CTR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-11499">
                          {cr.ctr}
                        </div>
                      </div>
                      <div data-id="element-11500">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-11501">
                          CR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-11502">
                          {cr.cr}
                        </div>
                      </div>
                    </div>
                    {idx === 0 && <Badge variant="success" data-id="element-11503">Winner</Badge>}
                  </div>)}
              </div>

              <div className="mt-6 flex justify-end" data-id="element-11504">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-11505">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-11506">
      {renderKpis()}
      {renderAiRecommendations()}
      {renderTable()}
      {renderModals()}
    </div>;
}